import { useMemo, useState } from 'react';

interface RoundStat {
  round: string;
  isFinal: boolean;
  points: number;
  gameCount: number;
}

interface PlayerStat {
  playerId: string;
  name: string;
  position: string;
  teamId: string;
  team: {
    id: string;
    name: string;
    abbreviation: string;
  };
  totalPoints: number;
  roundStats: RoundStat[];
}

interface PlayerStatsProps {
  playerStats: PlayerStat[];
  loading: boolean;
}

type SortField = 'name' | 'position' | 'team' | 'total' | `round-${number}`;
type SortDirection = 'asc' | 'desc';

export function PlayerStats({ playerStats, loading }: PlayerStatsProps) {
  const [positionFilter, setPositionFilter] = useState<string>('ALL');
  const [teamFilter, setTeamFilter] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('total');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Get unique positions for filter
  const positions = useMemo(() => {
    const unique = new Set(playerStats.map(p => p.position));
    return ['ALL', ...Array.from(unique).sort()];
  }, [playerStats]);

  // Get unique teams for filter
  const teams = useMemo(() => {
    const unique = new Map<string, string>();
    playerStats.forEach(p => {
      unique.set(p.team.abbreviation, p.team.name);
    });
    const sorted = Array.from(unique.entries()).sort((a, b) => a[0].localeCompare(b[0]));
    return [['ALL', 'All Teams'], ...sorted];
  }, [playerStats]);

  // Get list of rounds for column headers
  const rounds = useMemo(() => {
    if (playerStats.length === 0) return [];
    return playerStats[0].roundStats;
  }, [playerStats]);

  // Filter and sort data
  const filteredData = useMemo(() => {
    let filtered = playerStats;

    // Apply position filter
    if (positionFilter !== 'ALL') {
      filtered = filtered.filter(p => p.position === positionFilter);
    }

    // Apply team filter
    if (teamFilter !== 'ALL') {
      filtered = filtered.filter(p => p.team.abbreviation === teamFilter);
    }

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(query) ||
        p.team.name.toLowerCase().includes(query) ||
        p.team.abbreviation.toLowerCase().includes(query)
      );
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let aVal: any;
      let bVal: any;

      if (sortField === 'name') {
        aVal = a.name;
        bVal = b.name;
      } else if (sortField === 'position') {
        aVal = a.position;
        bVal = b.position;
      } else if (sortField === 'team') {
        aVal = a.team.abbreviation;
        bVal = b.team.abbreviation;
      } else if (sortField === 'total') {
        aVal = a.totalPoints;
        bVal = b.totalPoints;
      } else if (sortField.startsWith('round-')) {
        const roundIndex = parseInt(sortField.split('-')[1]);
        aVal = a.roundStats[roundIndex]?.points || 0;
        bVal = b.roundStats[roundIndex]?.points || 0;
      }

      if (typeof aVal === 'string') {
        return sortDirection === 'asc' 
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      } else {
        return sortDirection === 'asc' 
          ? aVal - bVal
          : bVal - aVal;
      }
    });

    return sorted;
  }, [playerStats, positionFilter, teamFilter, searchQuery, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection(field === 'total' ? 'desc' : 'asc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading player statistics...</p>
      </div>
    );
  }

  if (playerStats.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">No player statistics available yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
            Search Players
          </label>
          <input
            type="text"
            id="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name or team..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          />
        </div>
        <div>
          <label htmlFor="position" className="block text-sm font-medium text-gray-700 mb-1">
            Position
          </label>
          <select
            id="position"
            value={positionFilter}
            onChange={(e) => setPositionFilter(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          >
            {positions.map(pos => (
              <option key={pos} value={pos}>{pos}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="team" className="block text-sm font-medium text-gray-700 mb-1">
            Team
          </label>
          <select
            id="team"
            value={teamFilter}
            onChange={(e) => setTeamFilter(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          >
            {teams.map(([abbr, name]) => (
              <option key={abbr} value={abbr}>
                {abbr === 'ALL' ? 'All Teams' : `${abbr} - ${name}`}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Results count */}
      <div className="text-sm text-gray-600">
        Showing {filteredData.length} of {playerStats.length} players
      </div>

      {/* Table */}
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                onClick={() => handleSort('name')}
                className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              >
                Player {getSortIcon('name')}
              </th>
              <th
                onClick={() => handleSort('position')}
                className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              >
                Pos {getSortIcon('position')}
              </th>
              <th
                onClick={() => handleSort('team')}
                className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              >
                Team {getSortIcon('team')}
              </th>
              <th
                onClick={() => handleSort('total')}
                className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 bg-blue-50"
              >
                Total {getSortIcon('total')}
              </th>
              {rounds.map((round, index) => (
                <th
                  key={index}
                  onClick={() => handleSort(`round-${index}`)}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                >
                  {round.round}
                  {round.isFinal && ' (2x)'}
                  {getSortIcon(`round-${index}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredData.map((player, rowIndex) => (
              <tr key={player.playerId} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                  {player.name}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                  {player.position}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                  {player.team.abbreviation}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900 bg-blue-50">
                  {player.totalPoints.toFixed(1)}
                </td>
                {player.roundStats.map((round, index) => (
                  <td
                    key={index}
                    className={`px-4 py-3 whitespace-nowrap text-sm ${
                      round.points > 0 ? 'text-green-600 font-medium' : 'text-gray-400'
                    }`}
                  >
                    {round.points > 0 ? round.points.toFixed(1) : '—'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
